// src/utils/authReturn.js
//
// NZSC-4245: 로그인이 끊긴 사용자가 로그인이 필요한 화면에서 공용 오류 화면(/analyze/error)으로
// 떨어지던 것을 로그인 화면으로 돌리고, 로그인 뒤 보던 화면으로 되돌린다.
//
// web-core `src/lib/authReturn.ts` 와 **같은 계약**(저장소 키·형식)이다. Vue 화면이 오류로 가면 라우터
// 관문이, web-core 화면이 오류로 가면(또는 Vue 가 풀 로드로 보내면) 오류 화면이 같은 판단을 한다.
// 한쪽만 바꾸면 크로스앱 경로에서 만료 표시·복귀 주소가 사라진다.
//
// 이 모듈은 절대 예외를 밖으로 흘리지 않는다(저장소 차단 브라우저에서도 이동은 계속돼야 한다).

export const AUTH_RETURN_KEYS = {
  // 마지막으로 로그인에 성공한 방법(kakao|apple). 만료 정리(localStorage.clear) 뒤에도 되살린다.
  method: "nz.login.method",
  // 이 탭에서 로그인이 만료됐다는 표시 { at, recent }. 탭 범위라 sessionStorage.
  lost: "nz.auth.lost",
  // 애플 로그인 복귀 주소 { path, at }. 애플 nonce 는 서버 허용값만 받으므로 주소를 싣지 못한다.
  ret: "nz.auth.return",
  // 같은 화면으로 로그인 유도가 반복되는지 { path, count, at } — 로그인해도 계속 401 이면 오류 화면을 보여준다.
  bounce: "nz.auth.bounce",
  // 방금 로그인 연장(refresh)에 실패했다 { at } — 오류 화면으로 가려는 이동이 로그인 끊김 때문인지 판단.
  fail: "nz.auth.fail",
  // 만료 정리 때 이 탭에 맡긴 화면 상태 { email, at, state } — 같은 계정 재로그인 때만 되살린다.
  stash: "nz.auth.stash",
};

/** 오류 직전 API 실패가 "로그인 끊김"으로 인정되는 최대 경과 */
export const AUTH_FAILURE_MAX_AGE_MS = 10 * 1000;
/** 만료 표시를 "방금 만료"로 인정하는 최대 경과 */
export const AUTH_LOST_MAX_AGE_MS = 30 * 60 * 1000;
/** 애플 로그인 복귀 주소의 유효 시간(로그인 화면 진입 ~ 콜백) */
export const AUTH_RETURN_MAX_AGE_MS = 15 * 60 * 1000;
/** 이 시간 안에 같은 화면으로 로그인 유도가 이만큼 반복되면 더 보내지 않는다 */
export const AUTH_BOUNCE_WINDOW_MS = 10 * 60 * 1000;
export const AUTH_BOUNCE_LIMIT = 2;

const RETURN_STATE_PREFIX = "return_";
const MAX_RETURN_PATH = 1000;
// 로그인·오류 화면으로는 되돌리지 않는다(되돌리면 다시 로그인 화면이거나 오류 화면이다).
const NON_RETURNABLE = [
  /^\/login(\/|$|\?)/,
  /^\/analyze\/login(\/|$|\?)/,
  /^\/analyze\/apple\/login(\/|$|\?)/,
  /^\/analyze\/error(\/|$|\?)/,
  /^\/analyze\/freelogin(\/|$|\?)/,
];
const APPLE_RELAY_EMAIL = /@privaterelay\.appleid\.com$/i;

const store = kind => {
  try {
    return typeof window !== "undefined" ? window[kind] : null;
  } catch (e) {
    return null;
  }
};
const readJson = (kind, key) => {
  try {
    const st = store(kind);
    const raw = st ? st.getItem(key) : null;
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const writeJson = (kind, key, value) => {
  try {
    const st = store(kind);
    if (st) st.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* 저장 실패가 이동을 막아선 안 된다 */
  }
};
const remove = (kind, key) => {
  try {
    const st = store(kind);
    if (st) st.removeItem(key);
  } catch (e) {
    /* noop */
  }
};

/**
 * 우리 사이트 안 경로만 돌려준다. 외부 주소·프로토콜 상대 주소(//evil)·역슬래시 우회·로그인/오류 화면은 null.
 * `/next` 접두사(web-core 내부 경로)는 떼어 사용자에게 보이는 주소로 맞춘다.
 */
export function safeReturnPath(path) {
  if (typeof path !== "string" || !path.trim()) return null;
  const p = path.trim().replace(/^\/next(?=\/|\?|$)/, "") || "/";
  if (!p.startsWith("/") || p.startsWith("//") || p.includes("\\")) return null;
  if (p.length > MAX_RETURN_PATH) return null;
  // 제어문자(줄바꿈 등)가 섞인 값은 버린다
  for (let i = 0; i < p.length; i++) {
    const c = p.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return null;
  }
  if (NON_RETURNABLE.some(re => re.test(p))) return null;
  return p;
}

const toHex = text => {
  const bytes = unescape(encodeURIComponent(text));
  let out = "";
  for (let i = 0; i < bytes.length; i++) out += bytes.charCodeAt(i).toString(16).padStart(2, "0");
  return out;
};
const fromHex = hex => {
  if (!/^(?:[0-9a-f]{2})+$/.test(hex)) return null;
  let bytes = "";
  for (let i = 0; i < hex.length; i += 2) bytes += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
  try {
    return decodeURIComponent(escape(bytes));
  } catch (e) {
    return null;
  }
};

/**
 * 카카오 state 에 싣는 복귀 주소. 16진수로 싣는 이유: 콜백(AnalyzeLogin)이 state 에 "reels"·"loan"·
 * "consult"·"_report" 가 **포함**됐는지로 분기하는 곳이 있어, base64 처럼 영문자가 섞이면 우연히 걸린다.
 */
export function encodeReturnState(path) {
  const safe = safeReturnPath(path);
  return safe ? `${RETURN_STATE_PREFIX}${toHex(safe)}` : null;
}
export function isReturnState(state) {
  return typeof state === "string" && state.startsWith(RETURN_STATE_PREFIX);
}
/** state → 안전한 복귀 주소. 형식이 깨졌거나 안전하지 않으면 null */
export function decodeReturnState(state) {
  if (!isReturnState(state)) return null;
  return safeReturnPath(fromHex(state.slice(RETURN_STATE_PREFIX.length)));
}

/** 로그인 성공 시 방법을 기억한다(kakao|apple) */
export function rememberLoginMethod(method) {
  if (method !== "kakao" && method !== "apple") return;
  try {
    const st = store("localStorage");
    if (st) st.setItem(AUTH_RETURN_KEYS.method, method);
  } catch (e) {
    /* noop */
  }
}
export function readLoginMethod() {
  try {
    const st = store("localStorage");
    const v = st ? st.getItem(AUTH_RETURN_KEYS.method) : null;
    return v === "kakao" || v === "apple" ? v : null;
  } catch (e) {
    return null;
  }
}

/**
 * 기록이 없을 때(배포 전에 로그인한 사용자)의 보조 판단 — 화면이 저장해 둔 이메일이 애플 가림 이메일이면 애플.
 * 그 외에는 판단하지 않는다(피그마: 최근 로그인 기록을 찾을 수 없으면 말풍선을 지운다).
 */
export function guessLoginMethodFromVuex() {
  try {
    const st = store("localStorage");
    const raw = st ? st.getItem("vuex") : null;
    const email = raw ? JSON.parse(raw)?.userStore?._email : null;
    return typeof email === "string" && APPLE_RELAY_EMAIL.test(email.trim()) ? "apple" : null;
  } catch (e) {
    return null;
  }
}

/** 이 탭에서 로그인이 만료됐다고 표시한다. 만료 정리(localStorage.clear) **전에** 불러야 방법을 읽는다 */
export function markAuthLost() {
  const recent = readLoginMethod() || guessLoginMethodFromVuex();
  writeJson("sessionStorage", AUTH_RETURN_KEYS.lost, { at: Date.now(), recent: recent || undefined });
  return recent;
}
export function readAuthLost(now = Date.now()) {
  const r = readJson("sessionStorage", AUTH_RETURN_KEYS.lost);
  return r && typeof r.at === "number" && now - r.at <= AUTH_LOST_MAX_AGE_MS ? r : null;
}
export function clearAuthLost() {
  remove("sessionStorage", AUTH_RETURN_KEYS.lost);
}

/** 로그인 만료 화면 말풍선에 쓸 최근 로그인 방법. 모르면 null(말풍선 숨김) */
export function recentLoginMethod() {
  return readAuthLost()?.recent || readLoginMethod() || guessLoginMethodFromVuex();
}

/**
 * 로그인 연장(refresh)에 실패했다 = 이 브라우저에 유효한 로그인이 없다. 401 전부가 아니라 이 지점만 남긴다 —
 * 관리자 미리보기 링크 만료처럼 고객 로그인과 무관한 401 까지 로그인 화면으로 보내지 않기 위해서다.
 */
export function markAuthFailure() {
  writeJson("sessionStorage", AUTH_RETURN_KEYS.fail, { at: Date.now() });
}
/** 방금(오류 화면으로 가기 직전) 로그인 연장에 실패했나 */
export function isRecentAuthFailure(now = Date.now()) {
  const r = readJson("sessionStorage", AUTH_RETURN_KEYS.fail);
  return !!r && typeof r.at === "number" && now - r.at <= AUTH_FAILURE_MAX_AGE_MS && r.at - now <= 2000;
}
export function clearAuthFailure() {
  remove("sessionStorage", AUTH_RETURN_KEYS.fail);
}

/**
 * 같은 화면으로 로그인 유도가 반복되는지 센다. 로그인했는데도 그 화면이 계속 401 이면(로그인과 무관한
 * 서버 거절) 로그인 화면과 그 화면 사이를 무한히 오가지 않도록 오류 화면을 그대로 보여준다.
 * @returns 이번에 로그인 화면으로 보내도 되면 true
 */
export function admitLoginRedirect(path, now = Date.now()) {
  const prev = readJson("sessionStorage", AUTH_RETURN_KEYS.bounce);
  const same = prev && prev.path === path && now - prev.at <= AUTH_BOUNCE_WINDOW_MS;
  const count = same ? prev.count + 1 : 1;
  if (count > AUTH_BOUNCE_LIMIT) return false;
  writeJson("sessionStorage", AUTH_RETURN_KEYS.bounce, { path, count, at: same ? prev.at : now });
  return true;
}

/**
 * 로그인 끊김으로 오류 화면에 가려는 순간 대신 갈 로그인 화면 주소(쿼리 포함).
 * 만료(이 탭에서 방금 만료됐거나 만료된 토큰이 남아 있음)면 만료 문구판을, 아니면 일반 로그인 화면을 쓴다.
 * 보내면 안 되는 경우(반복 유도)는 null.
 */
export function loginRedirectFor(fromPath, { hasToken = false } = {}) {
  // 다방 인입 세션은 소셜 로그인이 없는 계정이다 — 로그인 화면이 아니라 기존 "다방에서 다시 열기" 안내를 따른다
  if (isDabangSession()) return null;
  const ret = safeReturnPath(fromPath);
  if (!admitLoginRedirect(ret || "/")) return null;
  const query = {};
  if (ret) query.return = ret;
  if (readAuthLost() || hasToken) query.reason = "expired";
  return { path: "/login", query };
}

/**
 * 라우터 관문 판단 — 오류 화면으로 가는 이동(to)을 어떻게 할지.
 *  - { abort: true }  방금 로그인 화면으로 돌렸는데 같은 화면의 다른 실패가 로그인 화면에서 또 오류로 보내는 경우
 *  - { login }        로그인 끊김 — 이 로그인 화면으로 돌린다(호출부가 표시 정리·계측·이동)
 *  - { login, repeat } 방금 돌린 로그인 화면으로 다시(반복 횟수 없이)
 *  - fullLoad: true    이 페이지에서 만료 정리가 일어남 — 정리(로그아웃 요청)가 끝난 뒤 풀 로드로 보낸다
 *  - null             해당 없음(지금처럼 오류 화면)
 */
export function authLostGuardDecision(to, from, { apiRecord, hasToken = false, now = Date.now() } = {}) {
  if (!to || to.path !== "/analyze/error") return null;
  // 로그인 화면 풀 로드가 이미 예약됐다 — 같은 화면의 다른 실패가 오류 화면으로 보내는 것은 전부 막는다
  if (loginFullLoadPending) return { abort: true };
  const decided = decideLogin(to, from, { apiRecord, hasToken, now });
  // 이 페이지에서 만료 정리가 일어났으면 메모리에 이전 회원 상태가 남아 있다 — 앱 안 이동은 그 상태를 다시
  // 저장소에 쓰게 되므로(vuex-persistedstate) 항상 정리가 끝난 뒤 풀 로드로 보낸다
  if (decided && decided.login && expiryCleanup) return { ...decided, fullLoad: true };
  return decided;
}

function decideLogin(to, from, { apiRecord, hasToken, now }) {
  if (justRedirectedToLogin(now)) {
    // 방금 로그인 화면으로 돌렸는데 같은 화면에서 동시에 실패한 다른 요청이 또 오류 화면으로 보내는 경우.
    // 이미 로그인 화면이면 이동만 막고, 로그인 화면 이동이 아직 진행 중(청크 로드 등)이면 같은 곳으로 다시 보낸다.
    if (from && from.path === "/login") return { abort: true };
    // 같은 화면에서 온 것일 때만(로그인 화면에서 X 로 다른 화면에 간 뒤의 무관한 오류는 그대로 오류 화면)
    const ret = safeReturnPath(from ? from.fullPath : null);
    if (lastLoginRedirect && ret && lastLoginRedirect.query && lastLoginRedirect.query.return === ret) {
      return { login: lastLoginRedirect, repeat: true };
    }
  }
  if (!isAuthLostError(apiRecord, now)) return null;
  const login = loginRedirectFor(from ? from.fullPath : null, { hasToken });
  return login ? { login } : null;
}

/** loginRedirectFor 결과를 풀 로드용 주소 문자열로 */
export function loginHref(route) {
  const params = new URLSearchParams();
  Object.keys(route.query || {}).forEach(k => params.set(k, route.query[k]));
  const qs = params.toString();
  return qs ? `${route.path}?${qs}` : route.path;
}

/** 애플 로그인 복귀 주소 — 로그인 화면이 저장하고 애플 콜백이 꺼낸다 */
export function saveAppleReturn(path) {
  const safe = safeReturnPath(path);
  if (safe) writeJson("sessionStorage", AUTH_RETURN_KEYS.ret, { path: safe, at: Date.now() });
  else remove("sessionStorage", AUTH_RETURN_KEYS.ret);
}
export function takeAppleReturn(now = Date.now()) {
  const r = readJson("sessionStorage", AUTH_RETURN_KEYS.ret);
  remove("sessionStorage", AUTH_RETURN_KEYS.ret);
  return r && typeof r.at === "number" && now - r.at <= AUTH_RETURN_MAX_AGE_MS ? safeReturnPath(r.path) : null;
}

/**
 * 맡기는 화면 상태 — 본인이 입력 중이던 신청 단계만(분석 신청 주소·주문, 주소미정 확정, 대출·보험·구독·계약 신청).
 * 제3자 정보가 든 모듈(theCheatStore 소유자 연락처·계좌, creditStore, reportStore, ownerHousePropertyStore, sciStore)과
 * 회원 정보(userStore)는 맡기지 않는다. 금융 사기 조회·리포트는 주소(seq)로 다시 열리므로 필요 없다.
 */
const STASH_MODULES = [
  "_items",
  "orderDeterminedStore",
  "loanGovStore",
  "insuranceStore",
  "subscribeStore",
  "contractStore",
];

/**
 * 만료 정리(localStorage.clear) 때 화면 상태("vuex")를 **이 탭에만** 잠시 맡겨 둔다. 로그인 뒤 보던 화면(무료 분석
 * 퍼널 등)이 이어지게 하되, 정리는 지금처럼 그대로 한다(localStorage 에는 되돌리지 않는다).
 * 되살리는 것은 로그인 콜백이 **같은 계정으로 다시 로그인한 것을 서버 응답(회원 이메일)으로 확인했을 때만**이다.
 * 다른 계정이면 버린다. 탭을 닫으면 사라지므로 같은 기기의 다음 사람에게 남지 않는다.
 */
export function stashScreenStateBeforeClear(now = Date.now()) {
  try {
    const st = store("localStorage");
    const raw = st ? st.getItem("vuex") : null;
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const email = typeof parsed?.userStore?._email === "string" ? parsed.userStore._email.trim() : "";
    if (!email) return; // 계정을 확인할 수 없으면 맡기지 않는다
    const state = {};
    STASH_MODULES.forEach(k => {
      if (parsed[k] !== undefined) state[k] = parsed[k];
    });
    // 로그인 플래그 등이 섞인 _state 에서는 주소미정 신청 진행 상태만
    if (parsed._state && parsed._state.addressDeterminedState !== undefined) {
      state._state = { addressDeterminedState: parsed._state.addressDeterminedState };
    }
    if (!Object.keys(state).length) return;
    writeJson("sessionStorage", AUTH_RETURN_KEYS.stash, { email, at: now, state });
  } catch (e) {
    /* 깨진 값·용량 초과는 맡기지 않는다 */
  }
}
/**
 * 맡겨 둔 화면 상태를 한 번만 꺼낸다. 같은 계정(이메일, 대소문자 무시)이고 30분 안이면 그 상태, 아니면 null.
 * 어느 쪽이든 맡긴 값은 지운다.
 */
export function takeScreenStateFor(email, now = Date.now()) {
  const r = readJson("sessionStorage", AUTH_RETURN_KEYS.stash);
  remove("sessionStorage", AUTH_RETURN_KEYS.stash);
  const mine = typeof email === "string" ? email.trim().toLowerCase() : "";
  if (!r || !mine || typeof r.at !== "number" || now - r.at > AUTH_LOST_MAX_AGE_MS) return null;
  if (typeof r.email !== "string" || r.email.toLowerCase() !== mine) return null;
  return r.state && typeof r.state === "object" ? r.state : null;
}

const isPlainObject = v => !!v && typeof v === "object" && !Array.isArray(v);
/**
 * 로그인 콜백용 — 서버가 돌려준 회원 이메일로 같은 계정임을 확인한 뒤 맡긴 화면 상태를 스토어에 되살린다.
 * 모듈 단위로 얕게 합친다("insuranceStore._register" 처럼 모듈 일부만 영속되는 것도 나머지를 지우지 않게).
 * @returns 되살렸으면 true
 */
export function restoreScreenState(vuexStore, email) {
  const saved = takeScreenStateFor(email);
  if (!saved || !vuexStore) return false;
  try {
    const next = { ...vuexStore.state };
    Object.keys(saved).forEach(k => {
      if (!(k in next)) return; // 지금 스토어에 없는 키는 들이지 않는다
      next[k] = isPlainObject(next[k]) && isPlainObject(saved[k]) ? { ...next[k], ...saved[k] } : saved[k];
    });
    vuexStore.replaceState(next);
    return true;
  } catch (e) {
    return false;
  }
}

/** 오류 화면으로 가려는 이번 이동이 로그인 끊김 때문인가 — 방금 연장이 거절됐고 마지막 API 실패도 401 */
export function isAuthLostError(apiRecord, now = Date.now()) {
  return (
    !!apiRecord &&
    Number(apiRecord.status) === 401 &&
    typeof apiRecord.at === "number" &&
    now - apiRecord.at <= AUTH_FAILURE_MAX_AGE_MS &&
    isRecentAuthFailure(now)
  );
}

// 방금 로그인 화면으로 돌렸다(앱 안 이동). 같은 화면에서 동시에 실패한 다른 요청의 처리부가 로그인 화면에서 다시
// 오류 화면으로 보내려 하면 막는다 — 막지 않으면 from 이 /login 이라 복귀 주소 없이 한 번 더 보내게 된다.
let loginRedirectedAt = 0;
let lastLoginRedirect = null;
export function noteLoginRedirect(now = Date.now(), login = null) {
  loginRedirectedAt = now;
  lastLoginRedirect = login;
}
export function justRedirectedToLogin(now = Date.now()) {
  return now - loginRedirectedAt <= AUTH_FAILURE_MAX_AGE_MS;
}

// 이 페이지에서 만료 정리가 일어났다(로그아웃 요청이 끝나면 이행되는 약속). api.js 가 정리를 시작하면서 —
// 대기 중이던 다른 요청의 실패 처리가 돌기 전에, 동기적으로 — 남긴다. 페이지가 새로 열리면 사라진다.
let expiryCleanup = null;
export function beginExpiryCleanup(done) {
  expiryCleanup = Promise.resolve(done).then(
    () => undefined,
    () => undefined,
  );
  return expiryCleanup;
}
let loginFullLoadPending = false;
/**
 * 만료 정리가 끝난 뒤 로그인 화면을 풀 로드한다. 정리 도중 화면 변경이 저장소에 이전 회원 상태를 다시 썼을 수
 * 있으므로 이동 직전에 한 번 더 비운다(로그인 방법만 남김). 예약된 복구 새로고침은 취소한다.
 */
export function goToLoginAfterExpiryCleanup(
  href,
  navigate = h => window.location.replace(h),
  beforeNavigate = () => {},
) {
  loginFullLoadPending = true;
  return (expiryCleanup || Promise.resolve()).then(() => {
    cancelRecoveryReload();
    // 풀 로드는 페이지를 파기한다 — 이탈 기록(exit_screen)·쌓인 계측을 먼저 보낸다(NZSC-3797 컷오버 이동과 같은 이유)
    try {
      beforeNavigate();
    } catch (e) {
      /* 계측 실패가 이동을 막아선 안 된다 */
    }
    purgeLocalSession();
    navigate(href);
  });
}

// 비울 때 남기는 계측 재전송 대기열 — 전송에 실패한 행동 이벤트(behaviorQueue RETRY_KEY). 지우면 그대로 유실된다.
const BEHAVIOR_RETRY_KEY = "nz_behavior_retry";
/**
 * 저장소를 비운다 — 로그인 방법(개인정보 아님, 다음 로그인 화면의 "최근 로그인")과 아직 못 보낸 계측 대기열만 남긴다.
 */
export function purgeLocalSession() {
  const method = readLoginMethod();
  let retry = null;
  try {
    const st = store("localStorage");
    if (st) {
      retry = st.getItem(BEHAVIOR_RETRY_KEY);
      st.clear();
      if (retry) st.setItem(BEHAVIOR_RETRY_KEY, retry);
    }
  } catch (e) {
    /* noop */
  }
  rememberLoginMethod(method);
}

/** 테스트용 — 모듈 상태 초기화 */
export function resetAuthReturnStateForTest() {
  expiryCleanup = null;
  loginFullLoadPending = false;
  loginRedirectedAt = 0;
  lastLoginRedirect = null;
  cancelRecoveryReload();
}

// 만료 복구 새로고침 예약. api.js 가 예약하고, 그 사이 화면이 오류 화면으로 가려 하면 라우터 관문이 가로채
// 새로고침 대신 로그인 화면으로 한 번에 보낸다(둘이 경쟁하면 오류 화면이 먼저 뜨던 것이 이번 결함의 원인).
let recoveryReload = null;
export function scheduleRecoveryReload(reload) {
  if (recoveryReload) clearTimeout(recoveryReload.timer);
  const entry = { timer: null };
  entry.timer = setTimeout(() => {
    if (recoveryReload === entry) recoveryReload = null;
    reload();
  }, 0);
  recoveryReload = entry;
}
/** 예약된 복구 새로고침을 취소한다. 예약이 있었으면 true */
export function cancelRecoveryReload() {
  if (!recoveryReload) return false;
  clearTimeout(recoveryReload.timer);
  recoveryReload = null;
  return true;
}

export function isDabangSession() {
  try {
    return store("sessionStorage")?.getItem("dabang_entered") === "1";
  } catch (e) {
    return false;
  }
}

/**
 * 로그인 성공 뒤 정리 — 만료 표시·로그인 끊김 표시·애플 복귀 주소는 이번 로그인으로 소비됐다.
 * (애플 콜백은 복귀 주소를 먼저 꺼낸 뒤 부른다. 맡긴 화면 상태는 복귀 분기가 계정 확인 후 꺼낸다.)
 */
export function finishAuthReturn(method, { returning = false } = {}) {
  rememberLoginMethod(method);
  clearAuthLost();
  clearAuthFailure();
  remove("sessionStorage", AUTH_RETURN_KEYS.ret);
  // 복귀가 아닌 로그인이면 맡긴 화면 상태(제3자 정보 포함)를 탭에 남겨 두지 않는다
  if (!returning) remove("sessionStorage", AUTH_RETURN_KEYS.stash);
}

/**
 * 카카오 로그인 콜백(AnalyzeLogin) — state 가 복귀 주소면 맡긴 화면 상태를 같은 계정일 때만 되살리고 갈 곳을 돌려준다.
 * 복귀 로그인이 아니면 null. 주소가 깨졌거나 우리 사이트 밖이면 홈.
 * @param email 방금 서버에서 받은 회원 이메일(acUserName)
 */
export function returnPathAfterKakaoLogin(state, vuexStore, email) {
  if (!isReturnState(state)) return null;
  restoreScreenState(vuexStore, email);
  return decodeReturnState(state) || "/main";
}

/**
 * 애플 로그인 콜백(AnalyzeAppleLogin) — 로그인 화면이 이 탭에 둔 복귀 주소를 꺼내고(nonce mypage 일 때만),
 * 로그인 정리를 한 뒤, 복귀면 서버에서 받은 이메일로 같은 계정일 때만 화면 상태를 되살린다. 복귀 주소 또는 null.
 * @param loadEmail 서버에서 회원 이메일을 받아 오는 함수(acUserName 뒤 스토어 값)
 */
export async function returnPathAfterAppleLogin({ nonce, status }, vuexStore, loadEmail) {
  const returnPath = nonce === "mypage" ? takeAppleReturn() : null; // 꺼낸 뒤 정리 — 순서 주의
  finishAuthReturn("apple", { returning: !!returnPath });
  if (returnPath && status !== "탈퇴요청") {
    restoreScreenState(vuexStore, await loadEmail());
  }
  return returnPath;
}

